import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

/**
 * The single, canonical button for the whole app.
 * Pill-shaped, OrbitFlow design tokens. All pages and components use this —
 * do not introduce raw `<button>` styling or parallel button classes.
 */
const buttonVariants = cva(
  "inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-full font-medium ring-offset-[hsl(var(--background))] transition-all duration-150 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--of-violet)] focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "bg-[var(--of-violet)] !text-white shadow-sm hover:bg-[var(--of-violet-deep)]",
        destructive:
          "bg-[var(--of-coral)] !text-white shadow-sm hover:brightness-95",
        outline:
          "bg-[var(--of-bg-elev)] text-[var(--of-fg-1)] border border-[var(--of-border)] hover:bg-[var(--of-ink-04)] hover:border-[var(--of-border-strong)]",
        secondary:
          "bg-[var(--of-ink-06)] text-[var(--of-fg-1)] hover:bg-[var(--of-ink-08)]",
        ghost:
          "bg-transparent text-[var(--of-fg-2)] hover:bg-[var(--of-violet)] hover:!text-white",
        link:
          "bg-transparent text-[var(--of-violet)] underline-offset-4 hover:underline",
      },
      size: {
        default: "h-9 px-4 text-[13px] [&_svg]:size-4",
        sm: "h-8 px-3.5 text-xs [&_svg]:size-3.5",
        lg: "h-11 px-6 text-sm [&_svg]:size-4",
        icon: "h-9 w-9 [&_svg]:size-4",
        "icon-sm": "h-8 w-8 [&_svg]:size-3.5",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
